import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, Droplets, Plus, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import LiveDriverMapDialog from "@/components/LiveDriverMapDialog";

function Stat({ label, value, sub, testid, accent }) {
  return (
    <div className={`border border-border rounded-2xl p-6 shadow-[0_2px_12px_rgba(2,132,199,0.07)] ${accent || "bg-card"}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-display text-4xl font-black tracking-tight mt-3 text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2 font-mono">{sub}</div>}
    </div>
  );
}

function StockTile({ stats, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [amt, setAmt] = useState("");
  const low = Number(stats.chlorine_stock_gallons) < Number(stats.low_stock_threshold);

  const restock = async () => {
    const gallons = parseFloat(amt);
    if (!gallons || gallons <= 0) return toast.error("Enter gallons to add");
    try {
      await api.post("/inventory/restock", { gallons });
      toast.success(`Stock now ${gallons + Number(stats.chlorine_stock_gallons)} gal`);
      setAdding(false); setAmt(""); onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className={`border rounded-2xl p-6 shadow-[0_2px_12px_rgba(2,132,199,0.07)] ${low ? "border-destructive/60 bg-destructive/5" : "border-border bg-teal-50/70"}`} data-testid="stat-stock">
      <div className="flex items-center justify-between">
        <div className="overline flex items-center gap-1"><Droplets className="w-3 h-3" /> Chlorine on hand</div>
        {low && <span className="text-[10px] font-bold uppercase tracking-wider text-destructive bg-destructive/10 px-1.5 py-0.5">Low</span>}
      </div>
      <div className={`font-display text-4xl font-black tracking-tighter mt-3 mono-num ${low ? "text-destructive" : "text-foreground"}`}>
        {Math.round(Number(stats.chlorine_stock_gallons))}<span className="text-lg font-bold ml-1 text-muted-foreground">gal</span>
      </div>
      <div className="text-xs text-muted-foreground mt-2 font-mono">Alert below {Math.round(Number(stats.low_stock_threshold))} gal</div>
      {!adding ? (
        <Button size="sm" variant="outline" className="rounded-sm mt-3 h-8" onClick={() => setAdding(true)} data-testid="restock-open-button">
          <Plus className="w-3 h-3 mr-1" /> Restock
        </Button>
      ) : (
        <div className="flex gap-2 mt-3">
          <Input type="number" min="0" step="1" autoFocus placeholder="gallons" className="rounded-sm h-8 w-24 mono-num"
                 value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="restock-amount-input" />
          <Button size="sm" className="rounded-sm h-8" onClick={restock} data-testid="restock-confirm-button">Add</Button>
          <Button size="sm" variant="ghost" className="rounded-sm h-8" onClick={() => { setAdding(false); setAmt(""); }}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);

  const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!stats) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="overline">Overview</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-sm" onClick={() => setMapOpen(true)} data-testid="live-driver-map-button">
            <Navigation className="w-4 h-4 mr-2" /> Live driver map
          </Button>
          <div className="text-xs text-muted-foreground font-mono hidden sm:block">{new Date().toLocaleDateString()}</div>
        </div>
      </div>
      <LiveDriverMapDialog open={mapOpen} onOpenChange={setMapOpen} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat testid="stat-revenue" label="Revenue (paid)" value={fmtMoney(stats.revenue)} sub={`${stats.total_invoices} invoices total`} accent="bg-sky-50/80" />
        <Stat testid="stat-outstanding" label="Outstanding" value={fmtMoney(stats.outstanding)} sub={`${stats.unpaid_invoices} unpaid`} accent="bg-amber-50/80" />
        <Stat testid="stat-customers" label="Customers" value={stats.total_customers} sub={`${stats.scheduled_customers} on schedule`} accent="bg-violet-50/70" />
        <Stat testid="stat-invoices" label="Invoices" value={stats.total_invoices} sub="lifetime" accent="bg-rose-50/70" />
        <StockTile stats={stats} onChanged={load} />
      </div>

      <section className="border border-border rounded-2xl overflow-hidden bg-card shadow-[0_2px_12px_rgba(2,132,199,0.07)]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-display text-xl font-bold tracking-tight">Recent invoices</h2>
          <Link to="/admin/invoices" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="view-all-invoices">
            View all <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="divide-y divide-border overflow-x-auto">
          {stats.recent_invoices.length === 0 && (
            <div className="px-6 py-10 text-center text-muted-foreground text-sm">No invoices yet — create your first ticket.</div>
          )}
          {stats.recent_invoices.map((inv) => (
            <Link to={`/admin/invoices/${inv.id}`} key={inv.id} className="grid grid-cols-12 px-6 py-3 hover:bg-secondary/60 transition-colors min-w-[700px]" data-testid={`recent-invoice-${inv.number}`}>
              <div className="col-span-2 mono-num text-sm">{inv.number}</div>
              <div className="col-span-4 text-sm font-medium">{inv.customer_name}</div>
              <div className="col-span-3 text-sm text-muted-foreground mono-num">{fmtDate(inv.delivery_date)}</div>
              <div className="col-span-2 text-sm mono-num">{fmtMoney(inv.total)}</div>
              <div className="col-span-1 text-right">
                <span className={`text-xs font-bold uppercase tracking-wider ${inv.status === 'paid' ? 'text-success' : 'text-destructive'}`}>
                  {inv.status}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
